import React, {useEffect, useRef, useState} from 'react';
import {InferenceSession, Tensor} from '../../types-external/onnxruntime-web';
import {Parameter} from '../../types/model/framework/Parameter';
import {Application, SinglePageApplication} from '../../types/project/Application';
import {
  AppONNXPostProcessorScript,
  ModelONNXPostProcessorScript,
  PostProcessorScript,
} from '../../types/scripts/PostProcessorScript';
import {
  ModelONNXPreProcessorScript,
  PreProcessorScript,
} from '../../types/scripts/PreProcessorScript';
import {usePostProcessingScript} from '../PrePostProcessing/PostProcessing/usePostProcessingScript';
import {usePreProcessingScript} from '../PrePostProcessing/PostProcessing/usePreProcessingScript';
import {RuntimeModeType, setDeployConfig, useDeploy} from '../TestDeployView/useDeploy';
import {useInferenceSession} from './useInferenceSession';
import './ONNXRuntimeContainer.scss';
import {
  RealtimePostProcessingParameters,
  RealtimePreProcessingParameters,
  TestParametersAccordion,
} from '../TestDeployView/DeployConfigPanel/TestDeployParameters';
import {useIntl} from 'react-intl';
import {InputSource} from '../../types/deployment/InferenceInputSource';
import {getPostProcessorParameters} from '../../api/postprocessor/GetPostProcessorParameters';
import {getPreProcessorParameters} from '../../api/postprocessor/GetPreProcessorParameters';
import useSWRImmutable from 'swr/immutable';
import {
  DefaultMonitorConfig,
  EVENT_TYPE,
  VideoStream,
  VideoStreamController,
  VideoStreamEvent,
  VideoStreamObserver,
  VideoStreamSubject,
} from '../TestDeployView/HlsVideoStream';
import {useSelector} from 'react-redux';
import {RootState} from '../../store';
// @ts-ignore
import md5 from 'md5';

export type ONNXRuntimeContainerProps = {
  type?: InputSource;
  projectId: string;
  application: Application;
  spa?: SinglePageApplication;
  runtimeMode: RuntimeModeType;
  files?: File[];
  inProgress: boolean;
  url?: string;
  onFinish?: () => void;
  onRunning?: () => void;
  onError?: (message?: string) => void;
  configuredParams: Parameter[];
  displayRealtimeParameters: boolean;
};

export const FetchPreProcessor = (
  application?: Application,
  onError?: (message?: string) => void,
  viewOnly?: boolean
) => {
  const onErrorRef = useRef(onError);
  const transformationScriptName = application?.transformation?.filePaths?.find(
    isPathScript
  );
  return usePreProcessingScript({
    projectId: application?.projectId,
    id: application?.id,
    hasPreProcessor:
      application?.transformation !== undefined && transformationScriptName !== undefined,
    fileName: transformationScriptName,
    onError: onErrorRef.current,
    viewOnly,
  });
};

export const FetchModelPostProcessor = (
  application?: Application,
  onError?: (message?: string) => void,
  viewOnly?: boolean
) => {
  const onErrorRef = useRef(onError);
  const modelPostProcessor = application?.postProcessors?.find(
    pp => pp.postProcessorType === 'MODEL'
  );
  return usePostProcessingScript({
    projectId: application?.projectId,
    applicationId: application?.id,
    id: 'latest',
    hasPostProcessors: application?.postProcessors !== undefined,
    fileName: modelPostProcessor?.filePaths?.find(isPathScript),
    postProcessorType: 'MODEL',
    executionContainer: 'BROWSER',
    onError: onErrorRef.current,
    viewOnly,
  });
};
export const FetchAppPostProcessor = (
  application?: Application,
  onError?: (message?: string) => void,
  viewOnly?: boolean
) => {
  const onErrorRef = useRef(onError);
  const appPostProcessor = application?.postProcessors?.find(
    pp => pp.postProcessorType === 'APPLICATION'
  );
  return usePostProcessingScript({
    projectId: application?.projectId,
    applicationId: application?.id,
    id: 'latest',
    hasPostProcessors: appPostProcessor !== undefined,
    fileName: appPostProcessor?.filePaths?.find(isPathScript),
    postProcessorType: 'APPLICATION',
    executionContainer: 'BROWSER',
    onError: onErrorRef.current,
    viewOnly,
  });
};
export const FetchSPAPostProcessor = (
  application: Application,
  spa: SinglePageApplication,
  onError?: (message?: string) => void,
  viewOnly?: boolean
) => {
  const onErrorRef = useRef(onError);
  const appPostProcessor = application.postProcessors?.find(
    pp => pp.id === spa.postProcessorId
  );
  return usePostProcessingScript({
    projectId: application.projectId,
    applicationId: spa.id,
    id: spa.postProcessorId,
    hasPostProcessors: appPostProcessor !== undefined,
    fileName: appPostProcessor?.filePaths?.find(isPathScript),
    postProcessorType: 'APPLICATION',
    executionContainer: 'BROWSER',
    onError: onErrorRef.current,
    viewOnly,
  });
};

export function supportsImages(
  modelPreProcessorScript?: PreProcessorScript,
  modelPostProcessorScript?: PostProcessorScript,
  appPostProcessorScript?: PostProcessorScript | null
): boolean {
  if (!modelPreProcessorScript || !modelPostProcessorScript) {
    return false;
  }
  return (
    'preprocessImage' in modelPreProcessorScript &&
    'postprocessOutputData' in modelPostProcessorScript &&
    (!appPostProcessorScript || 'postprocessImage' in appPostProcessorScript)
  );
}

export function supportsVideo(
  modelPreProcessorScript?: PreProcessorScript,
  modelPostProcessorScript?: PostProcessorScript,
  appPostProcessorScript?: PostProcessorScript | null
): boolean {
  if (!modelPreProcessorScript || !modelPostProcessorScript) {
    return false;
  }
  return (
    'preprocessVideoFrame' in modelPreProcessorScript &&
    'postprocessOutputDataAsync' in modelPostProcessorScript &&
    (!appPostProcessorScript || 'postprocessVideo' in appPostProcessorScript)
  );
}

export const ONNXRuntimeContainer = ({
  type,
  projectId,
  application,
  spa,
  runtimeMode,
  files,
  inProgress,
  onFinish,
  onError,
  onRunning,
  url,
  configuredParams,
  displayRealtimeParameters,
}: ONNXRuntimeContainerProps) => {
  const intl = useIntl();
  const [, dispatch] = useDeploy();

  const applicationId = application.id;

  const modelPreProcessorScript = FetchPreProcessor(application, onError);
  const modelPostProcessorScript = FetchModelPostProcessor(application, onError);
  const appPostProcessorScript =
    application && spa
      ? FetchSPAPostProcessor(application, spa, onError)
      : FetchAppPostProcessor(application, onError);
  const [streamReady, setStreamReady] = useState(false);
  const [streamConnected, setStreamConnected] = useState(false);
  const [streamSubject, setStreamSubject] = useState<VideoStreamSubject>();
  const [videoController, setVideoController] = useState<VideoStreamController>();
  const [videoUrl, setVideoUrl] = useState<string>();
  const [localVideoStream, setLocalVideoStream] = useState<MediaStream>();
  const serverConfig = useSelector((state: RootState) => state.config);

  const {data: postProcessorParametersData} = useSWRImmutable(
    projectId && applicationId
      ? [projectId, applicationId, spa?.id, '/postprocessor/parameters/get']
      : null,
    () => getPostProcessorParameters({projectId, applicationId, spaId: spa?.id})
  );

  const {data: preProcessorParametersData} = useSWRImmutable(
    projectId && applicationId
      ? [projectId, applicationId, '/preprocessor/parameters/get']
      : null,
    () => getPreProcessorParameters({projectId, applicationId})
  );

  const postProcessorParameters = postProcessorParametersData?.body;
  const preProcessorParameters = preProcessorParametersData?.body;

  const containerRef = useRef<HTMLDivElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const [videoRef, setVideoRef] = useState<HTMLVideoElement>();
  const onErrorRef = useRef(onError);
  const onFinishRef = useRef(onFinish);
  const onRunningRef = useRef(onRunning);

  const inputShape = application.model.frameworkParameters.inputShape;

  // Initialize session
  const session = useInferenceSession(
    projectId,
    applicationId,
    runtimeMode,
    onErrorRef.current
  );

  class OnnxStreamObserver extends VideoStreamObserver {
    handleEvent(event: VideoStreamEvent) {
      switch (event.type) {
        case EVENT_TYPE.CONNECTED:
          onRunningRef.current?.();
          setStreamConnected(true);
          break;
        case EVENT_TYPE.CONNECTION_FAILED:
          setStreamConnected(false);
          setStreamReady(false);
          break;
        case EVENT_TYPE.REMOTE_STREAM:
          onRunningRef.current?.();
          setStreamReady(true);
          break;
        case EVENT_TYPE.ERROR:
          if (event.message) {
            event.message = `Streaming error: ${event.message}`;
          } else {
            event.message = `Streaming error. Please try again.`;
          }
          if (onError) {
            onError(event.message);
          }
          setStreamReady(false);
          break;
        case EVENT_TYPE.STREAM_FINISHED:
          if (onFinish && type !== 'IMAGES') {
            onFinish();
          }
          setStreamReady(false);
          break;
        default:
          break;
      }
    }
  }

  useEffect(() => {
    let videoStreamSubject = new VideoStreamSubject();
    setStreamSubject(videoStreamSubject);
    const controller = new VideoStreamController();
    videoStreamSubject.addListener('OnnxStreamObserver', new OnnxStreamObserver());
    setVideoController(controller);

    const cleanup = () => {
      videoStreamSubject.raiseStreamFinishEvent();
    };
    window.addEventListener('beforeunload', cleanup);

    return () => {
      window.removeEventListener('beforeunload', cleanup);
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    async function rewriteUrl(inputUrl?: string) {
      setVideoUrl(inputUrl);
    }
    rewriteUrl(url);
  }, [applicationId, type, url]);

  useEffect(() => {
    let localStream: MediaStream;

    async function startWebCamera() {
      try {
        const mediaConstraints = {
          audio: false,
          video: true,
        };
        localStream = await navigator.mediaDevices.getUserMedia(mediaConstraints);
        setLocalVideoStream(localStream);
      } catch (e) {
        onErrorRef.current?.('Error initializing web camera');
        console.error('Error initializing web camera', e);
      }
    }

    if (!session) {
      return;
    }

    if (type === 'WEB_CAMERA') {
      startWebCamera();

      return () => {
        if (localStream) {
          localStream.getTracks().forEach(track => track.stop());
        }
      };
    } else if (
      (type === 'IP_CAMERA' || type === 'FILE') &&
      videoUrl &&
      streamSubject &&
      videoController
    ) {
      videoController.start({
        limitedDuration: !!spa,
        source: type,
        url: `${serverConfig.hlsServerUrl}/${md5(videoUrl)}/index.m3u8`,
        sourceUrl: videoUrl,
        streamModel: 'PROXIED_PUSH',
        projectId,
        serverConfig,
        monitorConfig: {
          ...DefaultMonitorConfig,
          monitorStream: false,
        },
        streamSubject,
      });
    }
  }, [
    type,
    spa,
    videoUrl,
    projectId,
    serverConfig,
    videoController,
    streamSubject,
    session,
  ]);

  useEffect(() => {
    if (type === 'WEB_CAMERA' && localVideoStream && videoRef) {
      videoRef.srcObject = localVideoStream || null;
    }
  }, [type, localVideoStream, videoRef]);

  // Run inference
  useEffect(() => {
    if (
      session &&
      modelPreProcessorScript &&
      modelPostProcessorScript &&
      appPostProcessorScript &&
      postProcessorParameters &&
      preProcessorParameters &&
      inProgress
    ) {
      if (type === 'IMAGES' && containerRef.current && files?.length) {
        if (
          supportsImages(
            modelPreProcessorScript,
            modelPostProcessorScript,
            appPostProcessorScript
          )
        ) {
          addParametersToWindow([
            ...(postProcessorParameters || []),
            ...(preProcessorParameters || []),
            ...(configuredParams || []),
          ]);
          // @ts-ignore
          runImageInference({
            session,
            preprocessImage: modelPreProcessorScript.preprocessImage,
            postprocessOutputData: (modelPostProcessorScript as ModelONNXPostProcessorScript)
              .postprocessOutputData,
            postprocessImage: (appPostProcessorScript as AppONNXPostProcessorScript)
              .postprocessImage,
            container: containerRef.current,
            files,
            inputShape,
            onError: onErrorRef.current,
            onFinish: onFinishRef.current,
          });
        } else {
          // post processors are not valid for images
          onErrorRef.current?.(
            'Pre and post processor(s) do not support image inference.'
          );
        }
      } else if (
        videoContainerRef.current &&
        videoRef &&
        (localVideoStream || streamReady)
      ) {
        if (
          supportsVideo(
            modelPreProcessorScript,
            modelPostProcessorScript,
            appPostProcessorScript
          )
        ) {
          addParametersToWindow([
            ...(postProcessorParameters || []),
            ...(preProcessorParameters || []),
            ...(configuredParams || []),
          ]);
          runVideoInference({
            session,
            preprocessVideoFrame: modelPreProcessorScript.preprocessVideoFrame,
            postprocessOutputDataAsync: (modelPostProcessorScript as ModelONNXPostProcessorScript)
              .postprocessOutputDataAsync,
            postprocessVideo: (appPostProcessorScript as AppONNXPostProcessorScript)
              .postprocessVideo,
            videoStream: localVideoStream,
            videoContainer: videoContainerRef.current,
            video: videoRef,
            inputShape,
            subject: streamSubject,
          });
        } else {
          // post processor are not valid for video
          onErrorRef.current?.(
            'Pre and post processor(s) do not support video streaming inference.'
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    type,
    session,
    configuredParams,
    inProgress,
    modelPreProcessorScript,
    modelPostProcessorScript,
    appPostProcessorScript,
    files,
    inputShape,
    postProcessorParameters,
    preProcessorParameters,
    localVideoStream,
    streamReady,
    streamSubject,
  ]);

  return (
    <div className="onnx-runtime">
      <div className="onnx-runtime__container" ref={containerRef}></div>
      {['WEB_CAMERA', 'FILE', 'IP_CAMERA'].includes(type ? type : '') && (
        <>
          <div className="onnx-runtime__video-container" ref={videoContainerRef}>
            <VideoStream
              className="ONNX"
              hlsRef={videoController?.hlsClient.hlsRef}
              streamConnected={streamConnected}
              streamReady={streamReady}
              monitor={videoController?.monitor}
              onLoadedVideoControl={videoElement => setVideoRef(videoElement)}
            />
          </div>
          {displayRealtimeParameters && (
            <>
              <TestParametersAccordion
                title={intl.formatMessage({id: 'test.form.rtpostprocessing'})}
                className="test-inference-results__accordion"
                expanded={true}
              >
                <RealtimePostProcessingParameters
                  className="test-inference-results__form"
                  projectId={projectId}
                  applicationId={applicationId}
                  spaId={spa?.id}
                  renderField={(field, param) => (
                    <div className="test-inference-results__field" key={param.id}>
                      {field}
                    </div>
                  )}
                  emptyMessage={
                    <div className="test-inference-results__empty">
                      {intl.formatMessage({id: 'test.form.postprocessing.empty'})}
                    </div>
                  }
                  onChange={realTimeParameters => {
                    dispatch(
                      setDeployConfig({postProcessorRealtimeParams: realTimeParameters})
                    );
                  }}
                />
              </TestParametersAccordion>
              <TestParametersAccordion
                title={intl.formatMessage({id: 'test.form.rtpreprocessing'})}
                className="test-inference-results__accordion"
                expanded={true}
              >
                <RealtimePreProcessingParameters
                  className="test-inference-results__form"
                  projectId={projectId}
                  applicationId={applicationId}
                  renderField={(field, param) => (
                    <div className="test-inference-results__field" key={param.id}>
                      {field}
                    </div>
                  )}
                  emptyMessage={
                    <div className="test-inference-results__empty">
                      {intl.formatMessage({id: 'test.form.preprocessing.empty'})}
                    </div>
                  }
                  onChange={realTimeParameters => {
                    dispatch(
                      setDeployConfig({preProcessorRealtimeParams: realTimeParameters})
                    );
                  }}
                />
              </TestParametersAccordion>
            </>
          )}
        </>
      )}
    </div>
  );
};

function addParametersToWindow(params: Parameter[]) {
  const paramsMap = params.reduce<Record<string, string | undefined | null>>(
    (acc, curr) => ({...acc, [curr.name]: getString(curr.value)}),
    {}
  );
  window.getConfigurationParameter = (key: string) => paramsMap[key];
}

function getString(value: any): string | undefined | null {
  return value !== undefined && value !== null ? value.toString() : value;
}

type VideoInferenceConfig = {
  session: InferenceSession;
  preprocessVideoFrame: ModelONNXPreProcessorScript['preprocessVideoFrame'];
  postprocessOutputDataAsync: ModelONNXPostProcessorScript['postprocessOutputDataAsync'];
  postprocessVideo: AppONNXPostProcessorScript['postprocessVideo'];
  videoStream?: MediaStream;
  videoContainer: HTMLDivElement;
  video: HTMLVideoElement;
  inputShape: number[];
  subject?: VideoStreamSubject;
};

async function runVideoInference({
  session,
  videoStream,
  video,
  videoContainer,
  preprocessVideoFrame,
  postprocessOutputDataAsync,
  postprocessVideo,
  inputShape,
  subject,
}: VideoInferenceConfig) {
  if (preprocessVideoFrame && postprocessOutputDataAsync && postprocessVideo) {
    const screenshotCanvas = document.createElement('canvas');
    const tempCanvas = document.createElement('canvas');
    const screenshotContext = screenshotCanvas.getContext('2d');
    const tempContext = tempCanvas.getContext('2d');
    let validated = false;
    let fatalError = false;

    if (screenshotContext && tempContext) {
      try {
        const videoFrameLoop = async () => {
          try {
            // video is no longer playing
            if (videoStream) {
              if (!videoStream.active) {
                return;
              }
            } else if (video.paused || video.ended) {
              return;
            }
            const {videoHeight, videoWidth} = video;
            if (videoHeight && videoWidth) {
              screenshotCanvas.width = videoWidth;
              screenshotCanvas.height = videoHeight;
              screenshotContext.drawImage(video, 0, 0, videoWidth, videoHeight);
              const preprocessedData = await preprocessVideoFrame(
                inputShape,
                screenshotContext,
                tempContext
              );
              const feeds: Record<string, Tensor> = {};
              feeds[session.inputNames[0]] = preprocessedData[0];
              let output;
              try {
                output = await session.run(feeds);
              } catch (e) {
                if (validated) {
                  // We skip validation errors (because of the known issues in ONNX) but throw all others.
                  fatalError = true;
                  throw e;
                }
              } finally {
                validated = true;
              }
              if (output) {
                const outputTensor = output[session.outputNames[0]];
                const postprocessedData = await Promise.resolve(
                  postprocessOutputDataAsync(outputTensor)
                );
                postprocessVideo(videoContainer, postprocessedData);
              }
            }
          } catch (e) {
            if (fatalError) {
              throw e;
            }
            console.log(e);
          }
          // Run at 30fps
          setTimeout(() => requestAnimationFrame(videoFrameLoop), 1000 / 30);
        };
        requestAnimationFrame(videoFrameLoop);
      } catch (e) {
        subject?.raiseInitErrorEvent('Streaming inference error. Please try again.');
        console.error('Error during ONNX model streaming inference.', e);
      }
    }
  }
}

type ImageInferenceConfig = {
  session: InferenceSession;
  preprocessImage: ModelONNXPreProcessorScript['preprocessImage'];
  postprocessOutputData: ModelONNXPostProcessorScript['postprocessOutputData'];
  postprocessImage: AppONNXPostProcessorScript['postprocessImage'];
  container: HTMLElement;
  files: File[];
  inputShape: number[];
  onError?: ONNXRuntimeContainerProps['onError'];
  onFinish?: ONNXRuntimeContainerProps['onFinish'];
};

async function runImageInference({
  session,
  preprocessImage,
  postprocessOutputData,
  postprocessImage,
  container,
  files,
  inputShape,
  onFinish,
  onError,
}: ImageInferenceConfig) {
  if (preprocessImage && postprocessOutputData && postprocessImage) {
    try {
      const urls: Map<string, string> = new Map<string, string>();
      const data: Map<string, any> = new Map<string, any>();
      await Promise.all(
        files.map(async file => {
          const fileObjectUrl = URL.createObjectURL(file);
          const preprocessedData = await Promise.resolve(
            preprocessImage(inputShape, fileObjectUrl)
          );
          const feeds: Record<string, Tensor> = {};
          feeds[session.inputNames[0]] = preprocessedData[0];
          const output = await session.run(feeds);
          const outputTensor = output[session.outputNames[0]];
          const postprocessedData = await Promise.resolve(
            postprocessOutputData(outputTensor)
          );
          urls.set(file.name, fileObjectUrl);
          data.set(file.name, postprocessedData);
        })
      );
      files.forEach(file => {
        postprocessImage(container, urls.get(file.name) || '', data.get(file.name));
      });
      onFinish?.();
    } catch (e) {
      onError?.('Image inference error. Please try again.');
      console.error('Error during ONNX model image inference.', e);
    }
  }
}

export const isPathScript = (path: string) => path !== 'metadata.yaml';
